import type {
	CompleteOnboardingRequest,
	OnboardingAddressInput,
	OnboardingInviteInput,
} from "@/adapters/api";
import type { OnboardingAddress } from "../types";
import type { OnboardingFlowData } from "../hooks/use-onboarding-flow";
import { dateToRFC3339, sanitizeArea } from "./format";

function toAddressInput(address: OnboardingAddress): OnboardingAddressInput {
	return {
		street: address.street.trim(),
		number: address.number.trim(),
		complement: address.complement.trim() || undefined,
		neighborhood: address.neighborhood.trim(),
		city: address.city.trim(),
		state: address.state.trim().toUpperCase(),
		zipcode: address.zipcode.trim(),
		lat: address.lat,
		lng: address.lng,
	};
}

export function buildOnboardingRequest(
	data: OnboardingFlowData,
): CompleteOnboardingRequest {
	const profile = data.profile?.type;
	if (!profile) {
		throw new Error("cannot build onboarding request without a profile");
	}
	if (!data.mode) {
		throw new Error("cannot build onboarding request without a mode");
	}

	const companyName =
		profile === "construction-company"
			? (data.profile?.companyName ?? null)
			: null;

	const invites: OnboardingInviteInput[] = data.invites.map((invite) => ({
		email: invite.email,
		role: invite.role,
	}));

	if (data.mode === "site-projects") {
		const site = data.site;
		return {
			profile,
			company_name: companyName,
			mode: data.mode,
			site: site
				? {
						name: site.name,
						address: toAddressInput(site.address),
						area: sanitizeArea(site.area),
						status: site.status,
					}
				: null,
			construction: null,
			invites,
		};
	}

	const project = data.singleProject;
	return {
		profile,
		company_name: companyName,
		mode: data.mode,
		site: null,
		construction: project
			? {
					name: project.name,
					type: project.type,
					color: project.color,
					address: toAddressInput(project.address),
					start_date: dateToRFC3339(project.startDate),
					end_date: dateToRFC3339(project.forecastEnd),
				}
			: null,
		invites,
	};
}
